import { Button, Kind } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { Role } from 'api/roles'
import Modal from 'components/ModalContainer'
import { RoleBadge } from 'components/RoleBadge'
import { getRoleBadgeVariant } from 'constants/roles'
import { buildTranslate } from 'locales'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'

type Props = {
  visible: boolean
  setIsVisible: (visible: boolean) => void
  roles: Role[]
}

type Step = 'choose' | 'pick-template'

const MODAL_ICON_SIZE = 32

const t = buildTranslate('dashboard_access.create_modal')

export const CreateRoleModal = ({ visible, setIsVisible, roles }: Props) => {
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const [step, setStep] = useState<Step>('choose')

  const hoverClass = css({
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    ':hover': {
      backgroundColor: '#edf0f3',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    },
  })

  const handleClose = () => {
    setIsVisible(false)
    setStep('choose')
  }

  const handleFromScratch = () => {
    handleClose()
    dispatch(push('/merchant_users/roles/new'))
  }

  const handleSelectTemplate = (roleId: number) => {
    handleClose()
    dispatch(push(`/merchant_users/roles/new?template=${roleId}`))
  }

  if (step === 'pick-template') {
    return (
      <Modal
        isOpen={visible}
        onClose={handleClose}
        onBack={() => setStep('choose')}
        hideCancel
        showBottomProceed={false}
      >
        <Text.Header3 className="mt-0 mb-l extra-bold">
          {t('choose_role_title')}
        </Text.Header3>
        <div className="d-flex flex-column" style={{ gap: '12px' }}>
          {roles.map(role => (
            <Button
              key={role.id}
              kind={Kind.SECONDARY_REVERSE}
              className={`border rounded p-m text-left w-100 ${hoverClass}`}
              onClick={() => handleSelectTemplate(role.id)}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <Text.Header4 className="m-0">{role.display_name}</Text.Header4>
                <RoleBadge variant={getRoleBadgeVariant(role)} />
              </div>
            </Button>
          ))}
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={visible}
      onClose={handleClose}
      hideCancel
      showBottomProceed={false}
    >
      <Text.Header3 className="mt-0 mb-l extra-bold">{t('title')}</Text.Header3>
      <div className="d-flex flex-column" style={{ gap: '16px' }}>
        <Button
          kind={Kind.SECONDARY_REVERSE}
          className={`border rounded p-m text-left w-100 ${hoverClass}`}
          onClick={handleFromScratch}
        >
          <div className="d-flex align-items-center w-100">
            <i
              className="fa fa-plus mr-m grey-50"
              aria-hidden="true"
              style={{ fontSize: MODAL_ICON_SIZE }}
            />
            <div>
              <Text.Header4 className="m-0">{t('from_scratch')}</Text.Header4>
              <Text.BodyText4 color="grey70" className="m-0">
                {t('from_scratch_description')}
              </Text.BodyText4>
            </div>
          </div>
        </Button>
        <Button
          kind={Kind.SECONDARY_REVERSE}
          className={`border rounded p-m text-left w-100 ${hoverClass}`}
          onClick={() => setStep('pick-template')}
        >
          <div className="d-flex align-items-center w-100">
            <i
              className="fa fa-copy mr-m grey-50"
              aria-hidden="true"
              style={{ fontSize: MODAL_ICON_SIZE }}
            />
            <div>
              <Text.Header4 className="m-0">{t('from_template')}</Text.Header4>
              <Text.BodyText4 color="grey70" className="m-0">
                {t('from_template_description')}
              </Text.BodyText4>
            </div>
          </div>
        </Button>
      </div>
    </Modal>
  )
}
